import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Card,
  Row,
  Col,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  EyeOutlined,
  LockOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchUsers,
  createUser,
  updateUser,
  deleteUser,
  fetchDepartements,
} from "../../../store/slices/referenceSlice";
import { RootState, AppDispatch } from "../../../store";
import { User } from "../../../types";
import { formatDateTime } from "../../../utils/dateUtils";
import { Container, ActionButton } from "./UserManagement.styles";

const { Option } = Select;

const UserManagement: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [form] = Form.useForm();
  const dispatch = useDispatch<AppDispatch>();
  const { users, departements, loading } = useSelector(
    (state: RootState) => state.references
  );

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [modalMode, setModalMode] = useState<"create" | "edit" | "view">(
    "create"
  );

  useEffect(() => {
    const loadUsers = async () => {
      const result = await dispatch(fetchUsers());
      if (fetchUsers.fulfilled.match(result)) {
        setTotalUsers(result.payload.length); // Utiliser la longueur du tableau
      }
    };
    loadUsers();
    dispatch(fetchDepartements());
  }, [dispatch]);

  const handleCreate = () => {
    setModalMode("create");
    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({ role: "visiteur", is_active: true });
    setIsModalVisible(true);
  };

  const handleEdit = (user: User) => {
    setModalMode("edit");
    setEditingUser(user);
    form.setFieldsValue(user);
    setIsModalVisible(true);
  };

  const handleView = (user: User) => {
    setModalMode("view");
    setEditingUser(user);
    form.setFieldsValue(user);
    setIsModalVisible(true);
  };

  const handleDelete = (user: User) => {
    Modal.confirm({
      title: "Supprimer l'utilisateur",
      content: `Êtes-vous sûr de vouloir supprimer ${user.first_name} ${user.last_name} ?`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          console.log("Tentative de suppression de l'utilisateur:", user.id);

          const result = await dispatch(deleteUser(user.id));

          console.log("Résultat de deleteUser:", result);

          if (deleteUser.fulfilled.match(result)) {
            message.success("Utilisateur supprimé avec succès");
          } else if (deleteUser.rejected.match(result)) {
            console.log("Erreur capturée:", result.payload);

            const errorData = result.payload as any;

            if (errorData?.error) {
              Modal.error({
                title: errorData.error,
                content: (
                  <div>
                    <p>{errorData.message}</p>
                    {errorData.details && (
                      <div
                        style={{
                          marginTop: 12,
                          padding: 12,
                          background: "#f5f5f5",
                          borderRadius: 6,
                        }}
                      >
                        <strong>Détails :</strong>
                        <ul style={{ margin: "8px 0 0 20px" }}>
                          {errorData.details.assignments_count && (
                            <li>
                              {errorData.details.assignments_count}{" "}
                              assignation(s) active(s)
                            </li>
                          )}
                          {errorData.details.dossiers_count && (
                            <li>
                              {errorData.details.dossiers_count} dossier(s)
                              concerné(s)
                            </li>
                          )}
                          {errorData.details.managed_dossiers_count && (
                            <li>
                              {errorData.details.managed_dossiers_count}{" "}
                              dossier(s) sous gestion
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                ),
                width: 500,
              });
            } else {
              message.error(
                `Erreur: ${errorData?.message || "Suppression impossible"}`
              );
            }
          }
        } catch (error: any) {
          console.error("Erreur dans handleDelete:", error);
          message.error("Erreur lors de la suppression");
        }
      },
    });
  };

  const handleSubmit = async (values: any) => {
    try {
      if (modalMode === "create") {
        await dispatch(createUser(values));

        await dispatch(fetchUsers());
        message.success("Utilisateur créé avec succès");
      } else if (modalMode === "edit" && editingUser) {
        await dispatch(updateUser({ id: editingUser.id, ...values }));
        message.success("Utilisateur modifié avec succès");
      }

      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error("Erreur lors de l'opération");
    }
  };

  const getRoleColor = (role: string) => {
    const colors = {
      gestionnaire: "purple",
      visiteur: "blue",
      elaborateur: "green",
      validateur: "orange",
    };
    return colors[role as keyof typeof colors] || "default";
  };

  const getRoleText = (role: string) => {
    const texts = {
      gestionnaire: "Gestionnaire",
      visiteur: "Visiteur",
      elaborateur: "Élaborateur",
      validateur: "Validateur",
    };
    return texts[role as keyof typeof texts] || role;
  };

  const columns = [
    {
      title: "Nom complet",
      key: "name",
      width: 250, // Largeur fixe
      render: (user: User) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {user.first_name} {user.last_name}
          </div>
          <div style={{ fontSize: "12px", color: "#8c8c8c" }}>{user.email}</div>
        </div>
      ),
    },
    {
      title: "Rôle",
      dataIndex: "role",
      key: "role",
      width: 120, // Largeur fixe
      render: (role: string) => (
        <Tag color={getRoleColor(role)}>{getRoleText(role)}</Tag>
      ),
    },
    {
      title: "Département",
      dataIndex: "departement",
      key: "departement",
      width: 150, // Largeur fixe
      ellipsis: true,
      render: (departementId: number) => {
        if (!departementId) return "-";
        const dept = departements.find((d) => d.id === departementId);
        return dept ? dept.nom : `ID: ${departementId}`;
      },
    },
    {
      title: "Téléphone",
      dataIndex: "telephone",
      key: "telephone",
      width: 120, // Largeur fixe
    },
    {
      title: "Statut",
      dataIndex: "is_active",
      key: "is_active",
      width: 80, // Réduit
      render: (is_active: boolean) => (
        <Tag color={is_active ? "success" : "default"}>
          {is_active ? "Actif" : "Inactif"}
        </Tag>
      ),
    },
    {
      title: "MFA",
      dataIndex: "mfa_enabled",
      key: "mfa_enabled",
      width: 60,
      align: "center" as const,
      render: (mfa_enabled: boolean) => (
        <div
          style={{
            fontSize: "16px",
            color: mfa_enabled ? "#52c41a" : "#d9d9d9",
          }}
        >
          {mfa_enabled ? <LockOutlined /> : <UnlockOutlined />}
        </div>
      ),
    },
    {
      title: "Date création",
      dataIndex: "date_creation",
      key: "date_creation",
      width: 140, // Réduit
      render: (date: string) => formatDateTime(date),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120, // Réduit
      fixed: "right" as const,
      render: (_: any, user: User) => (
        <Space size="small">
          <Tooltip title="Voir">
            <ActionButton
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleView(user)}
            />
          </Tooltip>
          <Tooltip title="Modifier">
            <ActionButton
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(user)}
            />
          </Tooltip>
          <Tooltip
            title={
              user.role !== "gestionnaire"
                ? "Supprimer"
                : "Attention: vérifiez les assignations"
            }
          >
            <ActionButton
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(user)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];
  return (
    <Container>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <h1>Gestion des utilisateurs</h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
          size="large"
        >
          Nouvel utilisateur
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{
            current: currentPage, 
            pageSize: 20, 
            total: totalUsers, 
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total) => `Total: ${total} utilisateurs`,
            onChange: setCurrentPage, 
          }}
        />
      </Card>

      <Modal
        title={
          modalMode === "create"
            ? "Nouvel utilisateur"
            : modalMode === "edit"
            ? "Modifier l'utilisateur"
            : "Détails de l'utilisateur"
        }
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        footer={
          modalMode === "view" ? (
            <Button onClick={() => setIsModalVisible(false)}>Fermer</Button>
          ) : (
            [
              <Button key="cancel" onClick={() => setIsModalVisible(false)}>
                Annuler
              </Button>,
              <Button
                key="submit"
                type="primary"
                onClick={() => form.submit()}
                loading={loading}
              >
                {modalMode === "create" ? "Créer" : "Modifier"}
              </Button>,
            ]
          )
        }
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          disabled={modalMode === "view"}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="first_name"
                label="Prénom"
                rules={[
                  { required: true, message: "Le prénom est obligatoire" },
                ]}
              >
                <Input placeholder="Prénom" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="last_name"
                label="Nom"
                rules={[{ required: true, message: "Le nom est obligatoire" }]}
              >
                <Input placeholder="Nom" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "L'email est obligatoire" },
              { type: "email", message: "Format email invalide" },
            ]}
          >
            <Input placeholder="Email" />
          </Form.Item>

          {modalMode === "create" && (
            <Form.Item
              name="password"
              label="Mot de passe"
              rules={[
                { required: true, message: "Le mot de passe est obligatoire" },
              ]}
            >
              <Input.Password placeholder="Mot de passe" />
            </Form.Item>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="role"
                label="Rôle"
                rules={[{ required: true, message: "Le rôle est obligatoire" }]}
              >
                <Select placeholder="Sélectionner un rôle">
                  <Option value="gestionnaire">Gestionnaire</Option>
                  <Option value="visiteur">Visiteur</Option>
                  <Option value="elaborateur">Élaborateur</Option>
                  <Option value="validateur">Validateur</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="telephone" label="Téléphone">
                <Input placeholder="Téléphone" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="departement" label="Département">
            <Select
              placeholder="Sélectionner un département"
              loading={loading}
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {departements.map((dept) => (
                <Option key={dept.id} value={dept.id}>
                  {dept.nom}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Utilisateur actif"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Container>
  );
};

export default UserManagement;
